// Durchschnittliche EEG-Verguetung fuer PV-Anlagen.
//
// Das EEG verguetet nicht pauschal nach Anlagengroesse, sondern ANTEILIG je
// Leistungsband (§ 48 Abs. 2 EEG 2023: "Durchschnitt der anzulegenden Werte
// fuer die jeweiligen Leistungsanteile"). Eine 60-kWp-Dachanlage bekommt also
// nicht durchgaengig den 40-kW-Satz, sondern:
//     10 kW zu 12,22 + 30 kW zu 10,24 + 20 kW zu 10,24  =  10,57 ct/kWh
// Genau diesen Mischwert liefert dieses Modul.
//
// Zwei Verguetungsarten, abhaengig von der Anlagengroesse:
//   bis 100 kWp  -> Einspeiseverguetung (§ 21 Abs. 1 i.V.m. §§ 48, 49, 53 EEG)
//   ab  100 kWp  -> Marktpraemie / anzulegender Wert (§ 20 EEG), weil ab dieser
//                   Groesse die Direktvermarktung verpflichtend ist (§ 21b EEG)
//
// STAND: Inbetriebnahme 1. August 2026 bis 31. Dezember 2026.
//
// ACHTUNG, das sieht nach einem Fehler aus und ist keiner: Die Saetze sinken
// halbjaehrlich (1. Februar / 1. August), rechnerisch waere also der
// 31.01.2027 das Ende. Zum 01.01.2027 aendert sich das EEG aber grundlegend,
// deshalb endet die Geltung dieser Saetze mit dem Jahr. Festlegung des
// Fachbereichs (21.09.2026) - bitte `gueltigBis` NICHT auf '2027-01-31'
// zurueckstellen. Genau das ist hier schon zweimal vorgeschlagen worden, weil
// der Grund nirgends stand.
//
// Bei Ablauf dieses Zeitraums gehoeren hier
// neue Werte hinein. Dieselben Tarife liegen gespiegelt in
// api/_lib/pvsolExtractor.js sowie im Projekt-Cockpit
// (src/lib/investors/eeg-tariff.ts, src/lib/expose/exposeWirtschaftlichkeit.js) -
// bei einer Aenderung alle vier Stellen nachziehen.

/**
 * Zeitraum, fuer den die hinterlegten Saetze gelten.
 *
 * Geprueft am 16.09.2026 gegen die Veroeffentlichung der Bundesnetzagentur
 * (bundesnetzagentur.de → Fachthemen → Erneuerbare Energien → EEG-Foerderung);
 * alle Saetze stimmten dabei ueberein.
 */
export const EEG_STAND = {
  gueltigAb: '2026-08-01',
  gueltigBis: '2026-12-31',
  bezeichnung: 'Inbetriebnahme 1. August 2026 bis 31. Dezember 2026',
  fundstelle: '§ 20 bzw. § 21 Abs. 1 i.V.m. §§ 48, 49, 53 Abs. 1 EEG 2023',
  geprueftAm: '2026-09-16',
  quelle: 'https://www.bundesnetzagentur.de/DE/Fachthemen/ElektrizitaetundGas/ErneuerbareEnergien/EEG_Foerderung/start.html',
};

/** Wie viele Tage vor Ablauf soll vorgewarnt werden? */
export const VORWARNUNG_TAGE = 7;

/**
 * Sind die hinterlegten Saetze noch aktuell?
 *
 * Die Foerdersaetze werden halbjaehrlich abgesenkt. Laeuft der Zeitraum ab,
 * rechnet das Tool sonst stillschweigend mit veralteten Werten weiter - und
 * ein zu hoch angesetzter Tarif zieht sich durch jedes Angebot, ohne dass es
 * jemandem auffaellt.
 *
 * Deshalb zwei Stufen: eine Woche vorher eine Vorwarnung, damit noch Zeit zum
 * Nachtragen bleibt, und nach Ablauf eine deutliche Meldung. Eine Warnung, die
 * erst am Tag des Ablaufs kommt, hilft niemandem - dann sind die falschen Werte
 * schon im naechsten Angebot.
 *
 * @param {Date} [stichtag=new Date()] Vergleichsdatum (fuer Tests setzbar)
 * @returns {{ zustand: 'gueltig'|'laeuftAus'|'abgelaufen'|'nochNichtGueltig',
 *             aktuell: boolean, meldung: string, tageBisAblauf: number|null }}
 */
export function pruefeAktualitaet(stichtag = new Date()) {
  const heute = stichtag instanceof Date ? stichtag : new Date(stichtag);
  const tag = heute.toISOString().slice(0, 10);

  if (tag > EEG_STAND.gueltigBis) {
    return {
      zustand: 'abgelaufen',
      aktuell: false,
      tageBisAblauf: 0,
      meldung: `Die hinterlegten Fördersätze galten bis zum ${formatiereDatum(EEG_STAND.gueltigBis)} `
        + 'und sind seitdem überholt. Die EEG-Sätze sinken halbjährlich – bitte die aktuellen Werte '
        + 'bei der Bundesnetzagentur nachtragen. Bis dahin rechnet das Tool zu hoch.',
    };
  }

  if (tag < EEG_STAND.gueltigAb) {
    return {
      zustand: 'nochNichtGueltig',
      aktuell: false,
      tageBisAblauf: null,
      meldung: `Die hinterlegten Fördersätze gelten erst ab dem ${formatiereDatum(EEG_STAND.gueltigAb)}. `
        + 'Für eine frühere Inbetriebnahme gelten andere Sätze.',
    };
  }

  const tageBisAblauf = tageZwischen(tag, EEG_STAND.gueltigBis);

  if (tageBisAblauf <= VORWARNUNG_TAGE) {
    const wann = tageBisAblauf === 0
      ? 'heute'
      : (tageBisAblauf === 1 ? 'morgen' : `in ${tageBisAblauf} Tagen`);
    return {
      zustand: 'laeuftAus',
      aktuell: true,
      tageBisAblauf,
      meldung: `Die hinterlegten Fördersätze laufen ${wann} aus `
        + `(gültig bis ${formatiereDatum(EEG_STAND.gueltigBis)}). `
        + 'Bitte die neuen Werte bei der Bundesnetzagentur abholen und nachtragen, '
        + 'bevor das erste Angebot mit falschen Sätzen rausgeht.',
    };
  }

  return { zustand: 'gueltig', aktuell: true, tageBisAblauf, meldung: '' };
}

/** Ganze Tage zwischen zwei ISO-Datumsangaben (beide als UTC-Mitternacht gelesen). */
function tageZwischen(vonIso, bisIso) {
  const von = Date.parse(`${vonIso}T00:00:00Z`);
  const bis = Date.parse(`${bisIso}T00:00:00Z`);
  if (!Number.isFinite(von) || !Number.isFinite(bis)) return 0;
  return Math.max(0, Math.round((bis - von) / 86400000));
}

function formatiereDatum(isoDatum) {
  const [jahr, monat, tag] = String(isoDatum).split('-');
  return `${tag}.${monat}.${jahr}`;
}

/** Ab dieser Groesse ist Direktvermarktung Pflicht - dann gilt die Marktpraemie. */
export const MARKTPRAEMIE_AB_KWP = 100;

/** Oberhalb dieser Groesse gibt es keinen gesetzlichen Anspruch mehr (Ausschreibung). */
export const AUSSCHREIBUNG_AB_KWP = 1000;

/**
 * Verguetungsstufen in ct/kWh.
 *
 * `ab_kwp` ist die UNTERE Grenze des Leistungsbands. Die Tabellen im
 * EEG-Merkblatt nennen dagegen die obere Grenze ("bis 40 kW") - das Band
 * "bis 40" entspricht hier also `ab_kwp: 10`.
 */
const TARIFE = {
  // Anlagen auf Gebaeuden oder Laermschutzwaenden (§ 48 Abs. 2, 2a EEG 2023)
  gebaeude: {
    einspeiseverguetung: {
      voll: [
        { ab_kwp: 0, ct_kwh: 12.22 },
        { ab_kwp: 10, ct_kwh: 10.24 },
        { ab_kwp: 40, ct_kwh: 10.24 },
      ],
      teil: [
        { ab_kwp: 0, ct_kwh: 7.70 },
        { ab_kwp: 10, ct_kwh: 6.66 },
        { ab_kwp: 40, ct_kwh: 5.44 },
      ],
    },
    marktpraemie: {
      voll: [
        { ab_kwp: 0, ct_kwh: 12.62 },
        { ab_kwp: 10, ct_kwh: 10.64 },
        { ab_kwp: 40, ct_kwh: 10.64 },
        { ab_kwp: 100, ct_kwh: 8.85 },
        { ab_kwp: 400, ct_kwh: 7.63 },
      ],
      teil: [
        { ab_kwp: 0, ct_kwh: 8.10 },
        { ab_kwp: 10, ct_kwh: 7.06 },
        { ab_kwp: 40, ct_kwh: 5.84 },
        { ab_kwp: 100, ct_kwh: 5.84 },
        { ab_kwp: 400, ct_kwh: 5.84 },
      ],
    },
  },

  // Sonstige Anlagen (§ 48 Abs. 1 EEG 2023) - im Wesentlichen Freiflaechen.
  // Hier gibt es KEINE Staffelung: ein einheitlicher Satz ueber die gesamte
  // Leistung, und Voll- wie Teileinspeisung werden gleich verguetet.
  sonstige: {
    einspeiseverguetung: {
      voll: [{ ab_kwp: 0, ct_kwh: 6.19 }],
      teil: [{ ab_kwp: 0, ct_kwh: 6.19 }],
    },
    marktpraemie: {
      voll: [{ ab_kwp: 0, ct_kwh: 6.59 }],
      teil: [{ ab_kwp: 0, ct_kwh: 6.59 }],
    },
  },
};

/** Welche Verguetungsart gilt bei dieser Groesse? */
export function ermittleVerguetungsart(kwp) {
  return (Number(kwp) || 0) >= MARKTPRAEMIE_AB_KWP ? 'marktpraemie' : 'einspeiseverguetung';
}

/**
 * Liefert die Verguetungsstufen fuer eine Konstellation.
 * Rueckgabe ist stets eine Kopie, damit Aufrufer die Tabellen nicht veraendern.
 */
export function holeStufen({ kwp, anlagenart = 'gebaeude', einspeiseart = 'voll' } = {}) {
  const art = TARIFE[anlagenart] ? anlagenart : 'gebaeude';
  const verguetungsart = ermittleVerguetungsart(kwp);
  const richtung = einspeiseart === 'teil' ? 'teil' : 'voll';

  return TARIFE[art][verguetungsart][richtung].map((stufe) => ({ ...stufe }));
}

/**
 * Berechnet die durchschnittliche EEG-Verguetung.
 *
 * @param {object} eingabe
 * @param {number} eingabe.kwp          Installierte Leistung in kWp
 * @param {'gebaeude'|'sonstige'} [eingabe.anlagenart='gebaeude']
 *        'gebaeude' = Dach oder Laermschutzwand, 'sonstige' = Freiflaeche
 * @param {'voll'|'teil'} [eingabe.einspeiseart='voll']
 *        Bei EN:POWER ist Volleinspeisung der Standard.
 * @returns {{
 *   kwp: number,
 *   durchschnitt: number,
 *   verguetungsart: string,
 *   verguetungsartLabel: string,
 *   anlagenart: string,
 *   einspeiseart: string,
 *   baender: Array<{vonKwp:number, bisKwp:number, anteilKwp:number, satz:number, ertragsanteil:number}>,
 *   hinweise: string[],
 *   stand: object,
 *   aktualitaet: {zustand: string, aktuell: boolean, meldung: string, tageBisAblauf: number|null},
 * }}
 */
export function berechneDurchschnittlicheEegVerguetung({
  kwp,
  anlagenart = 'gebaeude',
  einspeiseart = 'voll',
} = {}) {
  const leistung = Number(kwp) || 0;
  const art = TARIFE[anlagenart] ? anlagenart : 'gebaeude';
  const richtung = einspeiseart === 'teil' ? 'teil' : 'voll';
  const verguetungsart = ermittleVerguetungsart(leistung);
  const hinweise = [];

  // Frueh ermitteln: Der Ablaufhinweis gilt unabhaengig davon, ob ueberhaupt
  // eine Leistung eingegeben wurde - auch ein leeres Formular soll melden,
  // dass die Saetze demnaechst nicht mehr stimmen.
  const aktualitaet = pruefeAktualitaet();

  if (leistung <= 0) {
    return {
      aktualitaet,
      kwp: 0,
      durchschnitt: 0,
      verguetungsart,
      verguetungsartLabel: beschreibeVerguetungsart(verguetungsart),
      anlagenart: art,
      einspeiseart: richtung,
      baender: [],
      hinweise: [
        'Bitte eine Anlagenleistung größer als 0 kWp angeben.',
        ...(aktualitaet.zustand === 'gueltig' ? [] : [aktualitaet.meldung]),
      ],
      stand: EEG_STAND,
    };
  }

  const stufen = TARIFE[art][verguetungsart][richtung];

  // Oberhalb von 1 MW endet die gesetzliche Verguetung: Solche Anlagen muessen
  // an der Ausschreibung teilnehmen, der Zuschlagswert ersetzt dann den Tarif.
  // Das wird ausdruecklich gemeldet, statt den Durchschnitt still zu verwaessern.
  const verguetbar = Math.min(leistung, AUSSCHREIBUNG_AB_KWP);
  if (leistung > AUSSCHREIBUNG_AB_KWP) {
    hinweise.push(
      `Nur die ersten ${AUSSCHREIBUNG_AB_KWP} kWp sind gesetzlich vergütet. `
      + `Für die darüber liegenden ${runde(leistung - AUSSCHREIBUNG_AB_KWP, 1)} kWp gilt das `
      + 'Ausschreibungsverfahren – dort zählt der Zuschlagswert, nicht dieser Tarif.',
    );
  }

  const baender = [];
  let summeCtMalKwp = 0;

  stufen.forEach((stufe, index) => {
    const von = stufe.ab_kwp;
    const bis = index + 1 < stufen.length ? stufen[index + 1].ab_kwp : AUSSCHREIBUNG_AB_KWP;
    const anteil = Math.max(0, Math.min(verguetbar, bis) - von);
    if (anteil <= 0) return;

    summeCtMalKwp += anteil * stufe.ct_kwh;
    baender.push({
      vonKwp: von,
      bisKwp: bis,
      anteilKwp: runde(anteil, 2),
      satz: stufe.ct_kwh,
      // Wie stark schlaegt dieses Band im Durchschnitt durch?
      ertragsanteil: 0, // wird unten gefuellt, sobald die Summe feststeht
    });
  });

  // Der Durchschnitt bezieht sich auf die verguetete Leistung. Bei Anlagen ueber
  // 1 MW waere ein Bezug auf die Gesamtleistung irrefuehrend - der Wert saehe
  // niedriger aus, als er fuer den verguetbaren Teil tatsaechlich ist.
  const durchschnitt = verguetbar > 0 ? summeCtMalKwp / verguetbar : 0;

  baender.forEach((band) => {
    band.ertragsanteil = summeCtMalKwp > 0
      ? runde((band.anteilKwp * band.satz) / summeCtMalKwp * 100, 1)
      : 0;
  });

  if (art === 'sonstige') {
    hinweise.push('Freiflächenanlagen werden ohne Staffelung vergütet – ein einheitlicher Satz über die gesamte Leistung.');
  }

  if (verguetungsart === 'marktpraemie') {
    hinweise.push('Ab 100 kWp ist die Direktvermarktung verpflichtend. Der Wert ist der anzulegende Wert der Marktprämie, nicht die ausgezahlte Vergütung.');
  }

  // Zuletzt und damit am auffaelligsten: Sind die Saetze ueberhaupt noch gueltig?
  // Nicht auf `aktuell` pruefen - beim nahenden Ablauf steht das noch auf true,
  // die Vorwarnung wuerde sonst verschluckt.
  if (aktualitaet.zustand !== 'gueltig') {
    hinweise.push(aktualitaet.meldung);
  }

  return {
    kwp: leistung,
    durchschnitt: runde(durchschnitt, 4),
    verguetungsart,
    verguetungsartLabel: beschreibeVerguetungsart(verguetungsart),
    anlagenart: art,
    einspeiseart: richtung,
    baender,
    hinweise,
    stand: EEG_STAND,
    // Damit die Oberflaeche den Ablauf hervorheben kann, statt ihn zwischen
    // den uebrigen Hinweisen untergehen zu lassen.
    aktualitaet,
  };
}

function beschreibeVerguetungsart(art) {
  return art === 'marktpraemie'
    ? 'Marktprämie (anzulegender Wert)'
    : 'Einspeisevergütung';
}

function runde(wert, stellen) {
  const faktor = 10 ** stellen;
  return Math.round(wert * faktor) / faktor;
}

/** Kurzform: nur der Durchschnittswert in ct/kWh. */
export function eegDurchschnittCtProKwh(kwp, anlagenart = 'gebaeude', einspeiseart = 'voll') {
  return berechneDurchschnittlicheEegVerguetung({ kwp, anlagenart, einspeiseart }).durchschnitt;
}
